import React from 'react';
import { ShieldCheck, FileText, ExternalLink } from 'lucide-react';

/**
 * Boeing Tool Coverage panel.
 *
 * Rendered on any product page that carries a `boeingCoverage` prop.
 * Purpose: give MRO tooling engineers and Part 145 quality managers
 * everything they need to justify the tool in their equivalency
 * program — the tool tag (SPL-* or COM-*), Boeing tasks that call it
 * out, the currently-listed Boeing parts, and the compliance framing
 * (Boeing does not certify third-party equipment; the tool tag is a
 * capability specification, and airline tool-equivalency programs
 * accept form-fit-functional equivalents under FAA Part 145.109(c)
 * and FAR 43.13(a)).
 *
 * Data shape (JS shape rather than TS for consistency with rest of app):
 * {
 *   toolTag:       string   e.g. 'SPL-1473' | 'COM-18702'
 *   toolTagLabel:  string   e.g. 'Boeing Special Tool' | 'Boeing Common Tool'
 *   status:        'primary' | 'equivalent'
 *   boeingParts:   [{ pn, supplier }]      // parts currently listed
 *                                          // under this tool tag in
 *                                          // the Boeing tool database
 *   tasks:         [{ number, name }]      // Boeing AMM tasks that
 *                                          // require the tool tag
 *   summary:       string  short one-line
 *   equivalencyStatement:  string  (only when status === 'equivalent')
 *   regulatoryRefs: [string]  e.g. ['FAA 14 CFR 145.109(c)', ...]
 * }
 */
const BoeingToolCoverage = ({ coverage }) => {
  if (!coverage) return null;
  const {
    toolTag,
    toolTagLabel,
    status = 'primary',
    boeingParts = [],
    tasks = [],
    summary,
    equivalencyStatement,
    regulatoryRefs = [],
  } = coverage;

  const isEquivalent = status === 'equivalent';

  return (
    <section
      className="py-14 sm:py-20 bg-slate-50 border-t border-slate-200"
      data-testid="boeing-tool-coverage"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Left rail — tool tag badge */}
          <div className="lg:col-span-1">
            <p className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-3">
              Boeing Tool Coverage
            </p>
            <div className="inline-flex items-center gap-2 rounded-full bg-[#1e2d3b] text-white px-4 py-1.5 text-sm font-semibold mb-3">
              <ShieldCheck className="w-4 h-4 text-blue-300" />
              {toolTagLabel}: {toolTag}
              {isEquivalent && (
                <span className="ml-1 rounded-full bg-white/10 px-2 py-0.5 text-[11px]">
                  Functional Equivalent
                </span>
              )}
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1e2d3b] leading-tight">
              Purpose-built for {toolTag}
            </h2>
            <div className="w-16 h-1 bg-gradient-to-r from-blue-600 to-orange-500 my-4"></div>
            <p className="text-slate-600 leading-relaxed text-base">{summary}</p>
          </div>

          {/* Right column — details grid */}
          <div className="lg:col-span-2 space-y-6">
            {/* Boeing tasks */}
            {tasks.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7">
                <h3 className="flex items-center gap-2 text-lg font-bold text-[#1e2d3b] mb-4">
                  <FileText className="w-5 h-5 text-blue-600" />
                  Boeing AMM Tasks That Reference {toolTag}
                </h3>
                <ul className="space-y-3">
                  {tasks.map((t, i) => (
                    <li
                      key={i}
                      className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3"
                      data-testid={`boeing-task-${i}`}
                    >
                      <span className="font-mono text-sm font-semibold text-blue-700 whitespace-nowrap">
                        Task {t.number}
                      </span>
                      <span className="text-slate-700 text-sm leading-snug">{t.name}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-slate-500 mt-4 leading-relaxed">
                  Task references sourced from Boeing AMM documentation supplied by our customers.
                  Coverage of individual tail-numbers depends on the operator's minor-model /
                  revision applicability.
                </p>
              </div>
            )}

            {/* Boeing parts listed under this tool tag */}
            {boeingParts.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7">
                <h3 className="text-lg font-bold text-[#1e2d3b] mb-4">
                  {boeingParts.length === 1
                    ? `Part number for ${toolTag}`
                    : `Parts currently listed under ${toolTag}`}
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 font-bold">
                        <th className="py-2 pr-4">Part Number</th>
                        <th className="py-2 pr-4">Supplier</th>
                        <th className="py-2">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {boeingParts.map((p, i) => (
                        <tr
                          key={i}
                          className="border-t border-slate-100"
                          data-testid={`boeing-part-${i}`}
                        >
                          <td className="py-2 pr-4 font-mono font-semibold text-[#1e2d3b] whitespace-nowrap">
                            {p.pn}
                          </td>
                          <td className="py-2 pr-4 text-slate-600 whitespace-nowrap">
                            {p.supplier}
                          </td>
                          <td className="py-2 text-slate-600">{p.notes || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Equivalency disclosure — only when not the primary Boeing-listed part */}
            {isEquivalent && equivalencyStatement && (
              <div className="bg-blue-50 rounded-2xl border border-blue-200 p-6 sm:p-7">
                <h3 className="text-lg font-bold text-[#1e2d3b] mb-3">
                  Tool Equivalency Statement
                </h3>
                <p className="text-slate-700 text-sm leading-relaxed">
                  {equivalencyStatement}
                </p>
                {regulatoryRefs.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-blue-200">
                    <h2 className="text-[11px] uppercase tracking-widest font-bold text-blue-700 mb-2">
                      Regulatory Framework
                    </h2>
                    <ul className="flex flex-wrap gap-2">
                      {regulatoryRefs.map((r, i) => (
                        <li
                          key={i}
                          className="text-xs bg-white text-blue-800 px-3 py-1 rounded-full border border-blue-200 font-medium"
                          data-testid={`regulatory-ref-${i}`}
                        >
                          {r}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <p className="text-[11px] text-slate-500 mt-4 italic">
                  Boeing does not certify third-party alternatives. Tool tags are capability
                  specifications; operators and Part 145 repair stations accept form-fit-functional
                  equivalents through documented tool-equivalency programs. Consult your quality
                  organization before adoption on any specific fleet.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BoeingToolCoverage;
